"use client";

import { Bot, Globe, Search, TrendingUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { marketingVerticalTabClass } from "~/components/siteglue/home/marketing-surface";

import { letterGrade, scoreTone } from "~/lib/siteglue/agent-ready/results-groups";
import {
  AUDIT_DEMO_AEO_COUNT,
  AUDIT_DEMO_ARO_COUNT,
  AUDIT_DEMO_CHANNELS,
  AUDIT_DEMO_CRO_COUNT,
  AUDIT_DEMO_DOMAIN,
  AUDIT_DEMO_LOOP_MS,
  AUDIT_DEMO_TAB_ORDER,
  type AuditDemoTabId,
  auditDemoCheckCountForTab,
  auditDemoCheckedCount,
  auditDemoCountTo,
  auditDemoReducedMotionElapsed,
  auditDemoResultReveal,
  auditDemoSceneAt,
  auditDemoSceneProgress,
  auditDemoShouldAutoplay,
  auditDemoSlideForTab,
  auditDemoTypedDomain,
  nextAuditDemoTab,
} from "~/lib/siteglue/how-it-works/audit-demo";
import {
  HOMEPAGE_SCAN_CTA,
  RESULTS_NOT_VERIFIED,
  RESULTS_STATUS,
  SCAN_CHANNELS_LABEL,
  SCAN_REPORT_AEO_HELP,
  SCAN_REPORT_AEO_TITLE,
  SCAN_REPORT_ARO_HELP,
  SCAN_REPORT_ARO_TITLE,
  SCAN_REPORT_CRO_HELP,
  SCAN_REPORT_CRO_TITLE,
  SCAN_REPORTS_LABEL,
  SCAN_WIZARD_BACK,
  SCAN_WIZARD_CONTINUE,
} from "~/lib/siteglue/landing-redesign/copy";
import { homepagePreviewSampleLabel, type HomepagePreviewSlide } from "~/lib/siteglue/landing-redesign/homepage-hero";
import { cn } from "~/lib/utils";

const TONE_TEXT = {
  ready: "text-emerald-600",
  partly: "text-amber-600",
  not: "text-red-600",
  empty: "text-slate-400",
} as const;

const TONE_BAR = {
  ready: "bg-emerald-500",
  partly: "bg-amber-500",
  not: "bg-red-500",
  empty: "bg-slate-300",
} as const;

const TONE_RESULT = {
  Verified: "text-emerald-700",
  Recommended: "text-amber-700",
  [RESULTS_NOT_VERIFIED]: "text-slate-500",
} as const;

const TAB_LABELS: Record<AuditDemoTabId, { short: string; full: string; icon: typeof Bot }> = {
  aro: { short: "ARO", full: "Agent Readiness", icon: Bot },
  aeo: { short: "AEO", full: "Answer Engine Optimization", icon: Search },
  cro: { short: "CRO", full: "Conversion Optimization", icon: TrendingUp },
};

const TAB_REPORT: Record<AuditDemoTabId, { title: string; help: string }> = {
  aro: { title: SCAN_REPORT_ARO_TITLE, help: SCAN_REPORT_ARO_HELP },
  aeo: { title: SCAN_REPORT_AEO_TITLE, help: SCAN_REPORT_AEO_HELP },
  cro: { title: SCAN_REPORT_CRO_TITLE, help: SCAN_REPORT_CRO_HELP },
};

function Chrome({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5">
      <div className="flex items-center gap-1.5" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      </div>
      <p className="truncate text-xs font-medium text-slate-500">{title}</p>
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
        Sample
      </span>
    </div>
  );
}

function ChoiceCard({
  title,
  help,
  checked,
  plain = false,
}: {
  title: string;
  help: string;
  checked: boolean;
  plain?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 px-4 py-3.5",
        plain
          ? ""
          : checked
            ? "rounded-2xl border border-[#4f81bd] bg-[#4f81bd]/[0.06]"
            : "rounded-2xl border border-slate-200 bg-white",
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{help}</span>
      </span>
      <span
        className={cn(
          "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border",
          checked ? "border-[#4f81bd] bg-[#4f81bd] text-white" : "border-slate-300 bg-white",
        )}
        aria-hidden
      >
        {checked ? "✓" : ""}
      </span>
    </div>
  );
}

function UrlScene({ typed, ready }: { typed: string; ready: boolean }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-2">
      <div className="flex w-full max-w-xl flex-col gap-2 rounded-[1.6rem] border border-slate-200 bg-white p-1.5 shadow-[0_10px_40px_rgba(15,23,42,0.06)] sm:flex-row sm:items-center sm:rounded-full">
        <div className="flex min-w-0 flex-1 items-center gap-2 px-3">
          <Globe className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          <span className="min-w-0 flex-1 truncate py-2.5 text-base text-slate-900">
            {typed || <span className="text-slate-400">yourcompany.com</span>}
          </span>
        </div>
        <span
          className={cn(
            "inline-flex h-11 items-center justify-center rounded-full bg-[#4f81bd] px-5 text-sm font-semibold text-white",
            ready && "ring-2 ring-[#4f81bd]/30",
          )}
        >
          {HOMEPAGE_SCAN_CTA}
        </span>
      </div>
    </div>
  );
}

function LineCheck({ label, checked }: { label: string; checked: boolean }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-800">
      <span
        className={cn(
          "inline-flex h-4 w-4 items-center justify-center rounded border text-[10px]",
          checked ? "border-[#4f81bd] bg-[#4f81bd] text-white" : "border-slate-300 bg-white text-transparent",
        )}
        aria-hidden
      >
        ✓
      </span>
      {label}
    </span>
  );
}

function ReportsScene({ tab, progress }: { tab: AuditDemoTabId; progress: number }) {
  const report = TAB_REPORT[tab];
  const channelsChecked = auditDemoCheckedCount(
    AUDIT_DEMO_CHANNELS.length,
    Math.min(1, Math.max(0, (progress - 0.2) * 1.6)),
  );
  return (
    <div>
      <p className="text-sm font-semibold text-slate-900">{SCAN_REPORTS_LABEL}</p>
      <div className="mt-3 space-y-2.5">
        <div className="rounded-2xl border border-[#4f81bd] bg-[#4f81bd]/[0.06]">
          <ChoiceCard title={report.title} help={report.help} checked plain />
          {tab === "cro" ? (
            <div className="border-t border-[#4f81bd]/15 px-4 pb-4 pt-3">
              <p className="text-sm font-semibold text-slate-900">{SCAN_CHANNELS_LABEL}</p>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                {AUDIT_DEMO_CHANNELS.map((channel, index) => (
                  <LineCheck key={channel.id} label={channel.label} checked={index < channelsChecked} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-4 flex justify-between">
        <span className="rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-800">
          {SCAN_WIZARD_BACK}
        </span>
        <span className="rounded-full bg-[#4f81bd] px-6 py-2.5 text-sm font-semibold text-white">
          {SCAN_WIZARD_CONTINUE}
        </span>
      </div>
    </div>
  );
}

function ScanScene({ tab, progress }: { tab: AuditDemoTabId; progress: number }) {
  const total = auditDemoCheckCountForTab(tab);
  const done = auditDemoCountTo(total, Math.min(1, progress * 1.15));
  const label = TAB_REPORT[tab].title;
  return (
    <div className="flex h-full flex-col items-center justify-center px-4 text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        Scanning {AUDIT_DEMO_DOMAIN}
      </p>
      <p className="mt-2 text-2xl font-semibold text-slate-900">Running {label}</p>
      <div className="mt-6 w-full max-w-md text-left">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-slate-800">{label}</span>
          <span className="tabular-nums text-slate-500">
            {done}/{total}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-[#4f81bd]"
            style={{ width: `${Math.max(4, Math.round((done / total) * 100))}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function ResultCard({ slide, progress }: { slide: HomepagePreviewSlide; progress: number }) {
  const scored = slide.score != null;
  const score = scored ? auditDemoCountTo(slide.score, progress) : null;
  const tone = scoreTone(score);
  const grade = letterGrade(score);
  const visibleRows = auditDemoCheckedCount(slide.rows.length, Math.min(1, progress * 1.15));

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
            {slide.kicker}
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-800">{RESULTS_STATUS(score)}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{slide.summary}</p>
        </div>
        <div className="flex items-end gap-3">
          <p className="text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            {score == null ? "—" : score}
            {score == null ? (
              <span className="ml-2 text-lg font-medium text-slate-400">{RESULTS_NOT_VERIFIED}</span>
            ) : (
              <span className="text-lg font-medium text-slate-400">/100</span>
            )}
          </p>
          <p className={`text-3xl font-semibold tracking-tight sm:text-4xl ${TONE_TEXT[tone]}`}>
            {grade}
          </p>
        </div>
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {slide.scoreLabel}
      </p>
      <div className="mt-3 space-y-3">
        {slide.rows.map((row, index) => {
          const visible = index < visibleRows;
          const width =
            row.score == null ? null : visible ? auditDemoCountTo(row.score, Math.min(1, progress * 1.2)) : 0;
          return (
            <div
              key={row.id}
              className={cn(
                "rounded-xl border border-slate-100 px-3 py-3 transition-opacity duration-300",
                visible ? "opacity-100" : "opacity-30",
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">{row.label}</p>
                <span className="flex shrink-0 items-center gap-2">
                  <p className={`text-sm font-semibold ${TONE_TEXT[scoreTone(width)]}`}>
                    {width == null ? "—" : `${width}/100`}
                  </p>
                  {visible ? (
                    <p className={`text-xs font-semibold ${TONE_RESULT[row.result]}`}>{row.result}</p>
                  ) : null}
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${TONE_BAR[scoreTone(width)]}`}
                  style={{ width: `${width == null ? 0 : Math.max(width, visible ? 4 : 0)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

function ResultsScene({ tab, elapsed }: { tab: AuditDemoTabId; elapsed: number }) {
  const reveal = auditDemoResultReveal(elapsed);
  const progress = tab === "aeo" ? reveal.aeo : tab === "cro" ? reveal.cro : reveal.aro;
  return (
    <div>
      <p className="mb-3 text-center text-xs font-medium text-slate-500">
        {homepagePreviewSampleLabel()}
      </p>
      <ResultCard slide={auditDemoSlideForTab(tab)} progress={progress} />
    </div>
  );
}

export function AuditDemoAnimation({
  className,
  fixedTab,
}: {
  className?: string;
  fixedTab?: AuditDemoTabId;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const elapsedRef = useRef(0);
  const lastElapsedRef = useRef(0);
  const tabRef = useRef<AuditDemoTabId>(fixedTab ?? "aro");
  const [elapsed, setElapsed] = useState(0);
  const [activeTab, setActiveTab] = useState<AuditDemoTabId>(fixedTab ?? "aro");
  const [playKey, setPlayKey] = useState(0);
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const sync = () => setPageVisible(document.visibilityState === "visible");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(Boolean(entry?.isIntersecting)),
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const autoplay = auditDemoShouldAutoplay({ reducedMotion, inView, pageVisible, paused });

  useEffect(() => {
    if (reducedMotion) {
      const frozen = auditDemoReducedMotionElapsed();
      elapsedRef.current = frozen;
      lastElapsedRef.current = frozen;
      setElapsed(frozen);
      return;
    }
    if (!autoplay) return;
    const startedAt = performance.now() - elapsedRef.current;
    let frame = 0;
    const tick = (now: number) => {
      const next = (now - startedAt) % AUDIT_DEMO_LOOP_MS;
      if (
        !fixedTab &&
        lastElapsedRef.current > next &&
        lastElapsedRef.current > AUDIT_DEMO_LOOP_MS * 0.75
      ) {
        const following = nextAuditDemoTab(tabRef.current);
        tabRef.current = following;
        setActiveTab(following);
      }
      lastElapsedRef.current = next;
      elapsedRef.current = next;
      setElapsed(next);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [autoplay, reducedMotion, playKey, fixedTab]);

  const selectTab = (tab: AuditDemoTabId) => {
    tabRef.current = tab;
    setActiveTab(tab);
    elapsedRef.current = 0;
    lastElapsedRef.current = 0;
    setElapsed(0);
    setPlayKey((key) => key + 1);
  };

  const scene = auditDemoSceneAt(elapsed);
  const typed = auditDemoTypedDomain(elapsed);
  const urlReady = scene === "url" && typed === AUDIT_DEMO_DOMAIN;
  const chromeTitle =
    scene === "results"
      ? `${TAB_REPORT[activeTab].title} · Preview`
      : scene === "scan"
        ? `${TAB_REPORT[activeTab].title} · Scanning`
        : TAB_REPORT[activeTab].title;

  return (
    <div
      ref={rootRef}
      className={cn("w-full", className)}
      data-testid="hiw-audit-demo"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <p className="sr-only">
        Animated preview of a SiteGlue audit with ARO, AEO, and CRO tabs. Enter a website, scan the
        selected audit, then review the sample report. AEO does not invent ChatGPT recommendations.
      </p>
      <div className={cn("grid gap-4", fixedTab ? "" : "lg:grid-cols-[16rem_minmax(0,1fr)]")}>
        {fixedTab ? null : (
        <div
          role="tablist"
          aria-label="How it works audits"
          aria-orientation="vertical"
          className="flex flex-col gap-1"
        >
          {AUDIT_DEMO_TAB_ORDER.map((id) => {
            const selected = activeTab === id;
            const label = TAB_LABELS[id];
            const Icon = label.icon;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`hiw-audit-tab-${id}`}
                aria-selected={selected}
                aria-controls="hiw-audit-panel"
                onClick={() => selectTab(id)}
                className={marketingVerticalTabClass(selected)}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden />
                <span>
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80">
                    {label.short}
                  </span>
                  <span className="block">{label.full}</span>
                </span>
              </button>
            );
          })}
        </div>
        )}
        <div
          id="hiw-audit-panel"
          role="tabpanel"
          aria-labelledby={`hiw-audit-tab-${activeTab}`}
          className="overflow-hidden rounded-[1.6rem] border border-slate-200 bg-slate-100 shadow-[0_18px_50px_rgba(15,23,42,0.12)]"
        >
          <Chrome title={chromeTitle} />
          <div className="bg-[#f7f6f3] p-3 sm:p-4">
            <div
              key={`${activeTab}-${scene}`}
              className={cn("min-h-[22rem] animate-fade-in", scene === "results" ? "min-h-0" : "")}
            >
              {scene === "url" ? <UrlScene typed={typed} ready={urlReady} /> : null}
              {scene === "reports" ? (
                <ReportsScene tab={activeTab} progress={auditDemoSceneProgress(elapsed, "reports")} />
              ) : null}
              {scene === "scan" ? (
                <ScanScene tab={activeTab} progress={auditDemoSceneProgress(elapsed, "scan")} />
              ) : null}
              {scene === "results" ? <ResultsScene tab={activeTab} elapsed={elapsed} /> : null}
            </div>
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-xs font-medium text-slate-500">
        {AUDIT_DEMO_ARO_COUNT} ARO checks · {AUDIT_DEMO_AEO_COUNT} AEO benchmarks · {AUDIT_DEMO_CRO_COUNT}{" "}
        CRO benchmarks
      </p>
    </div>
  );
}
